export abstract class FetchError<X = unknown> extends Error {
  readonly response: Response;
  readonly data: X;

  constructor(message: string, response: Response, data: X) {
    super(message || "Fetch error");
    this.response = response;
    this.data = data;
    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  isClientError(): boolean {
    return this.response.status >= 400 && this.response.status < 500;
  }

  isServerError(): boolean {
    return this.response.status >= 500 && this.response.status < 600;
  }

  getStatusCode(): number {
    return this.response.status;
  }
}

export class HttpError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || `HTTP error (${response.status})`, response, data);
    this.name = "HttpError";
  }
}

export class NetworkError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Network error", response, data);
    this.name = "NetworkError";
  }
}

// Timeout error (e.g., request took too long)
export class TimeoutError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Request timed out", response, data);
    this.name = "TimeoutError";
  }
}

// Authentication error (401 Unauthorized)
export class AuthenticationError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Authentication failed (401)", response, data);
    this.name = "AuthenticationError";
  }
}

// Authorization error (403 Forbidden)
export class AuthorizationError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Access forbidden (403)", response, data);
    this.name = "AuthorizationError";
  }
}

// Not found error (404 Not Found)
export class NotFoundError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Resource not found (404)", response, data);
    this.name = "NotFoundError";
  }
}

// Rate limit error (429 Too Many Requests)
export class RateLimitError<X = unknown> extends FetchError<X> {
  constructor(
    message: string,
    response: Response,
    data: X,
    retryAfter?: number,
  ) {
    super(message || "Rate limit exceeded (429)", response, data);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }

  readonly retryAfter?: number; // Optional retry-after header value (in seconds)
}

// Validation error (422 Unprocessable Entity)
export class ValidationError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Request validation failed (422)", response, data);
    this.name = "ValidationError";
  }
}

// Abort error (request canceled)
export class AbortError<X = unknown> extends FetchError<X> {
  constructor(message: string, response: Response, data: X) {
    super(message || "Request aborted", response, data);
    this.name = "AbortError";
  }
}
